/**
 * FlyerDeals hooks — TanStack Query wrappers around `FlyerDealsApi` (T12.0.15
 * Step 7).
 *
 * One query per `includeExpired` value — flipping the toggle on the Flyer
 * Deals page intentionally triggers a refetch with the new param so the
 * recently-expired window crosses the network boundary, not a client filter.
 *
 * T12.0.15.3 — `refetchIntervalMs` drives the cold-start poll: when a
 * household has just selected stores in a fresh FSA, an on-demand Flipp
 * ingestion is in flight server-side and `fetchAll` returns the stores with
 * empty `deals`. The Flyer Deals page passes a poll interval while it shows
 * the cold-start state so the deals appear the moment ingestion lands rows;
 * it passes `false` once deals resolve (or the cold-start window elapses).
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from './context';
import { keys } from './keys';

export function useFlyerDeals(
  includeExpired = false,
  refetchIntervalMs: number | false = false,
) {
  const api = useApi();
  return useQuery({
    queryKey: keys.FlyerDeals.all(includeExpired),
    queryFn: () => api.FlyerDeals.fetchAll({ includeExpired }),
    refetchInterval: refetchIntervalMs,
  });
}

/**
 * T55.3 — fit-ranked deals search. Disabled on a bar with no words at all
 * (mirrors this task's "search is additive" contract: an empty bar has no
 * search-specific meaning — the base `useFlyerDeals` view already covers
 * it), so the surface only fires this query once the shopper has typed
 * something.
 */
export function useDealsSearch(
  q: string,
  storeIds?: string[],
  rewrite?: 'off',
) {
  const api = useApi();
  const trimmed = q.trim();
  return useQuery({
    queryKey: keys.FlyerDeals.search(trimmed, storeIds, rewrite),
    queryFn: () =>
      api.FlyerDeals.search({
        q: trimmed,
        ...(storeIds ? { storeIds } : {}),
        ...(rewrite ? { rewrite } : {}),
      }),
    enabled: trimmed.length > 0,
  });
}

export function useDealsSearchSuggestions(q: string) {
  const api = useApi();
  const trimmed = q.trim();
  return useQuery({
    queryKey: keys.FlyerDeals.suggestions(trimmed),
    queryFn: () => api.FlyerDeals.searchSuggestions(trimmed),
    enabled: trimmed.length >= 2,
  });
}
